<script setup lang="ts">
import type { Price, ResultsPriceSelectProperties } from '@optionway/models';
import { ArrowRight, Info } from 'lucide-vue-next';

defineEmits(['selectResult']);

const { $appConfig } = useNuxtApp();
const config = $appConfig.app?.components?.resultsPriceSelect;

const prefix = 'components.results.priceSelect';

const properties = withDefaults(defineProps<ResultsPriceSelectProperties & Price & { loading?: boolean }>(), {
  pricing: undefined,
});

const componentConfig = computed<ResultsPriceSelectProperties>(() => {
  return {
    pricing: properties.pricing ?? config?.pricing ?? false,
  };
});

const averagePerPaxParts = computed(() => usePriceFormatParts(properties?.averagePerPax));

const { committedPaxCount } = useSearchCriteriaStore();

const sheetOpen = defineModel<boolean>('sheetOpen', { default: false });

const isMouse = useMediaQuery('(pointer: fine)');

const infoButtonReference = useTemplateRef('infoButtonReference');
const { focused: infoButtonFocused } = useFocus(infoButtonReference);

const openSheetOnTouch = () => {
  if (isMouse.value) {
    return;
  }
  infoButtonFocused.value = false;
  sheetOpen.value = true;
};
</script>

<template>
  <div
    class="flex h-full items-center justify-between font-normal md:justify-center md:rounded-lg md:border md:border-border md:p-4"
  >
    <UiCustomBottomSheet
      v-model:open="sheetOpen"
      :title="$t(`${prefix}.tooltip.title`)"
      :close-label="$t(`${prefix}.close`)"
    >
      <ResultsPriceDetails v-bind="properties" />
    </UiCustomBottomSheet>

    <div class="flex h-full items-center gap-3 md:w-full md:flex-col md:justify-center">
      <div class="flex flex-col text-left md:space-y-3 md:text-center">
        <!-- Total by pax -->
        <div>
          <UiSkeleton
            v-if="properties.loading"
            class="mt-1 h-6 w-28"
          />
          <div
            v-else
            class="inline-flex items-center justify-start gap-1"
          >
            <div class="flex items-baseline gap-1 md:flex-col md:items-center md:gap-0">
              <span
                class="font-semibold leading-none text-brand-primary-700"
                :aria-label="usePriceFormat(properties?.averagePerPax)"
                aria-atomic="true"
              >
                <span
                  class="text-2xl md:text-7"
                  aria-hidden="true"
                  >{{ averagePerPaxParts.integer }}</span
                ><span
                  class="text-xl md:text-xl"
                  aria-hidden="true"
                  >{{ averagePerPaxParts.decimal }}</span
                >
              </span>
              <span class="inline-flex items-center gap-1 text-xs text-text-secondary-600">
                <span class="md:hidden">{{ $t(`${prefix}.perPax`) }}</span>
                <span class="hidden md:inline">{{ $t(`${prefix}.perPaxFull`) }}</span>
                <UiTooltip>
                  <UiTooltipTrigger as-child>
                    <button
                      ref="infoButtonReference"
                      type="button"
                      :aria-label="$t(`${prefix}.details`)"
                      class="hidden items-center md:inline-flex"
                      @click="openSheetOnTouch"
                    >
                      <Info
                        class="h-3 w-3 text-text-secondary-600"
                        aria-hidden="true"
                      />
                    </button>
                  </UiTooltipTrigger>
                  <UiTooltipContent class="p-4">
                    <div class="min-w-72">
                      <ResultsPriceDetails
                        v-bind="properties"
                        dark
                      />
                    </div>
                  </UiTooltipContent>
                </UiTooltip>
              </span>
            </div>
          </div>
        </div>

        <!-- Total -->
        <div v-if="committedPaxCount > 1">
          <UiSkeleton
            v-if="properties.loading"
            class="mt-1 h-4 w-28"
          />
          <div
            v-else
            class="flex items-center gap-1 text-xs text-text-secondary-600 md:justify-center"
          >
            <span>{{ $t(`${prefix}.total`) }} : {{ usePriceFormat(properties?.total) }}</span>
          </div>
        </div>
      </div>

      <!-- Buy button (small) -->
      <div class="ml-auto flex h-full items-center md:hidden">
        <UiSkeleton
          v-if="properties.loading"
          class="h-9 w-9"
        />
        <UiButton
          v-else
          size="icon"
          :loading="componentConfig.pricing"
          :loading-label="$t('components.common.loading')"
          :disabled="properties.disabled"
          :aria-label="$t(`${prefix}.select`)"
          @click="$emit('selectResult')"
        >
          <ArrowRight aria-hidden="true" />
        </UiButton>
      </div>

      <!-- Buy button (large) -->
      <div class="hidden w-full md:block">
        <UiSkeleton
          v-if="properties.loading"
          class="h-9 w-full"
        />
        <UiButton
          v-else
          :loading="componentConfig.pricing"
          :loading-label="$t('components.common.loading')"
          :disabled="properties.disabled"
          class="w-full"
          @click="$emit('selectResult')"
        >
          {{ $t(`${prefix}.select`) }}
          <ArrowRight
            class="stroke-2"
            aria-hidden="true"
          />
        </UiButton>
      </div>
    </div>
  </div>
</template>
